/**
 * Annotations Types and Interfaces - WESTEER
 *
 * @version 2.0
 * @updated 2026-02-16
 * @module core/map-tools/annotations
 *
 * @description
 * Tipos e interfaces para el sistema de anotaciones mejorado.
 * Incluye sistema de categorización con colores.
 */

/**
 * Categorías de anotaciones
 */
export type AnnotationCategory =
  | 'note'       // Nota general
  | 'idea'       // Idea/brainstorming
  | 'task'       // Tarea pendiente
  | 'important'  // Importante/urgente
  | 'warning';   // Advertencia/precaución

/**
 * Metadata de cada categoría
 */
export interface CategoryMetadata {
  id: AnnotationCategory;
  label: string;
  icon: string; // Nombre del icono de lucide-react
  color: string;
  borderColor: string;
  bgColor: string;
  description: string;
}

/**
 * Configuración de todas las categorías
 */
export const ANNOTATION_CATEGORIES: Record<AnnotationCategory, CategoryMetadata> = {
  note: {
    id: 'note',
    label: 'Nota',
    icon: 'StickyNote',
    color: '#FFEB3B',      // Amarillo sticky clásico
    borderColor: '#FBC02D',
    bgColor: 'rgba(255, 235, 59, 0.08)',
    description: 'Nota general o comentario'
  },
  idea: {
    id: 'idea',
    label: 'Idea',
    icon: 'Lightbulb',
    color: '#FFD54F',      // Amarillo dorado
    borderColor: '#FFA000',
    bgColor: 'rgba(255, 213, 79, 0.08)',
    description: 'Idea o concepto para explorar'
  },
  task: {
    id: 'task',
    label: 'Tarea',
    icon: 'CheckSquare',
    color: '#81C784',      // Verde suave
    borderColor: '#4CAF50',
    bgColor: 'rgba(129, 199, 132, 0.08)',
    description: 'Acción o tarea pendiente'
  },
  important: {
    id: 'important',
    label: 'Importante',
    icon: 'AlertCircle',
    color: '#FF6B6B',      // Rojo/rosa suave
    borderColor: '#E53935',
    bgColor: 'rgba(255, 107, 107, 0.08)',
    description: 'Punto crítico o urgente'
  },
  warning: {
    id: 'warning',
    label: 'Advertencia',
    icon: 'AlertTriangle',
    color: '#FFB020',      // Naranja ámbar
    borderColor: '#F57C00',
    bgColor: 'rgba(255, 176, 32, 0.08)',
    description: 'Precaución o advertencia'
  }
} as const;

/**
 * Interfaz de anotación
 */
export interface Annotation {
  id: string;
  coordinates: [number, number]; // [lng, lat] GeoJSON format
  text: string;
  category: AnnotationCategory;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string; // Para futuro multi-usuario
}

/**
 * Props del componente AnnotationsTool
 */
export interface AnnotationsToolProps {
  map: any | null; // MapLibreMap
  isActive: boolean;
  onDeactivate: () => void;
}
